import { useMemo } from "react";
import FormItem from "@components/FormItem/FormItem";
import FormSection from "@components/FormSection/FormSection";
import type { VesselCondition } from "@features/vessel-call/vessel-call-status/MovementServices/form/utils";
import {
  FORM_FIELDS,
  LOADED_VESSEL_CONDITIONS,
  type VesselNominationOutletContext,
} from "@features/vessel-nomination/add/constants";
import type { VesselNominationFormData } from "@features/vessel-nomination/add/schema/vesselNominationFormSchema";
import { useFormattedReference } from "@hooks/useFormattedReference";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi";
import type { Cargo } from "@services/vessel-nomination/types";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import { LANG_BASE } from "../PreArrivalQuestionnaire";

const SERVICE = "bff-vessel";

const CargoOnboard = () => {
  const { t } = useTranslation();
  const { formData, updateFormData } = useOutletContext<VesselNominationOutletContext>();

  const { options: vesselConditions } = useFormattedReference<VesselCondition>(
    `${REFERENCE_DATA_TYPES.VESSEL_CONDITIONS}/search`,
    SERVICE,
  );

  const vesselConditionOptions = useMemo(
    () =>
      vesselConditions.map((condition) => ({
        label: String(condition.conditionDesc),
        value: String(condition.conditionCode),
      })),
    [vesselConditions],
  );

  const { options: cargoTypes } = useFormattedReference<Cargo>(
    `${REFERENCE_DATA_TYPES.CARGO}?cargoTypeCodes=LB,FO,CO`,
    SERVICE,
  );

  const cargoTypeOptions = useMemo(
    () =>
      cargoTypes.map((type) => ({
        label: String(type.cargoDescription),
        value: String(type.cargoCode),
      })),
    [cargoTypes],
  );

  const selectedVesselCondition = formData.watch(FORM_FIELDS.VESSEL_CONDITION);
  const isVesselConditionLoaded = useMemo(() => {
    return LOADED_VESSEL_CONDITIONS.includes(selectedVesselCondition);
  }, [selectedVesselCondition]);
  const cargoType = formData.watch(FORM_FIELDS.CARGO_TYPE);

  return (
    <FormProvider {...formData}>
      <FormSection
        title={t(`${LANG_BASE}.sections.cargo-onboard`)}
        className="grid-cols-1 lg:grid-cols-2 xl:grid-cols-4"
      >
        <div className="lg:col-span-2">
          <FormItem<VesselNominationFormData, typeof FORM_FIELDS.VESSEL_CONDITION>
            testId="nomination-vessel-condition-input"
            name={FORM_FIELDS.VESSEL_CONDITION}
            type="select"
            label={t(`${LANG_BASE}.fields.vessel-condition`)}
            onFieldChange={updateFormData}
            options={vesselConditionOptions}
          />
        </div>
        <span className="lg:col-span-2" />
        {isVesselConditionLoaded && (
          <>
            <div className="lg:col-span-2">
              <FormItem<VesselNominationFormData, typeof FORM_FIELDS.CARGO_TYPE>
                testId="nomination-cargo-type-input"
                name={FORM_FIELDS.CARGO_TYPE}
                type="autocomplete"
                label={t(`${LANG_BASE}.fields.cargo-type`)}
                onFieldChange={updateFormData}
                options={cargoTypeOptions}
              />
            </div>
            <FormItem<VesselNominationFormData, typeof FORM_FIELDS.CARGO_QUANTITY_M3>
              testId="nomination-cargo-quantity-m3-input"
              name={FORM_FIELDS.CARGO_QUANTITY_M3}
              type="number"
              label={t(`${LANG_BASE}.fields.cargo-quantity-m3`)}
              onFieldChange={updateFormData}
              maxDigits={12}
              decimals={3}
              thousandSeparator
              adornment="M3"
              canBeginWithZero={false}
            />
            <FormItem<VesselNominationFormData, typeof FORM_FIELDS.CARGO_QUANTITY>
              testId="nomination-cargo-quantity-input"
              name={FORM_FIELDS.CARGO_QUANTITY}
              type="number"
              label={t(`${LANG_BASE}.fields.cargo-quantity`)}
              onFieldChange={updateFormData}
              maxDigits={12}
              decimals={3}
              thousandSeparator
              adornment="MT"
              canBeginWithZero={false}
            />
            {cargoType === "OT" && (
              <div className="lg:col-span-2">
                <FormItem<VesselNominationFormData, typeof FORM_FIELDS.CARGO_TYPE_OTHER>
                  testId="nomination-cargo-type-other-input"
                  name={FORM_FIELDS.CARGO_TYPE_OTHER}
                  type="text"
                  label={t(`${LANG_BASE}.fields.cargo-type-other`)}
                  onFieldChange={updateFormData}
                />
              </div>
            )}
          </>
        )}
      </FormSection>
    </FormProvider>
  );
};

export default CargoOnboard;
